import React, { useState, useEffect, useCallback } from 'react';
import {
  FaFileSignature,
  FaCheckCircle,
  FaClock,
  FaCalendarAlt,
  FaRupeeSign,
  FaPercent,
  FaGripLines,
  FaHandshake,
  FaShieldAlt,
  FaBuilding,
  FaStore,
  FaUtensils,
  FaTruck,
  FaCreditCard,
  FaExclamationTriangle,
  FaLock,
  FaGavel,
  FaArrowLeft,
  FaPrint,
  FaDownload,
  FaShare,
  FaEdit,
  FaTimes,
  FaCheck,
  FaCircle,
  FaInfoCircle,
  FaSpinner
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getData, putData } from '../../../Services/Fetchnodeservices';

const MOUAgreement = ({ isDark = false }) => {
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [restaurant, setRestaurant] = useState(null);
  const [mouData, setMouData] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  // Schedule state
  const [scheduleMode, setScheduleMode] = useState('manual');
  const [shiftStart, setShiftStart] = useState('10:00');
  const [shiftEnd, setShiftEnd] = useState('22:00');
  const [isScheduleApproved, setIsScheduleApproved] = useState(false);
  const [isMouAccepted, setIsMouAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittingSchedule, setSubmittingSchedule] = useState(false);

  // 🎨 ZAPTODOOR ORANGE THEME CONFIGURATION
  const theme = {
    bg: isDark ? '#0f172a' : '#fff7ed',
    cardBg: isDark ? '#1e293b' : '#ffffff',
    text: isDark ? '#f8fafc' : '#0f172a',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    border: isDark ? 'rgba(249, 115, 22, 0.2)' : '#fed7aa',
    primary: '#f97316',
    primaryLight: '#fb923c',
    primaryGradient: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
    accentBg: isDark ? 'rgba(249, 115, 22, 0.1)' : '#fff7ed',
    success: '#16a34a',
    warning: '#f59e0b',
    shadow: isDark ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(249, 115, 22, 0.1)',
  };

  // Get user data from localStorage
  const getUserData = () => {
    try {
      const data = localStorage.getItem('userData');
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error('Error parsing user data:', e);
      return null;
    }
  };

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const userData = getUserData();
      const phone = userData?.phone || userData?.contact || '';
      const restaurantId = userData?.restaurantId || userData?.id || '';
      
      console.log('User Data:', userData);
      console.log('Phone:', phone);
      console.log('Restaurant ID:', restaurantId);

      // Fetch MOU, Restaurant, and Subscriptions in parallel
      const [resMou, resRest, resSubs] = await Promise.all([
        getData('vandersuperadmin/mou/latest'),
        getData(`vandersuperadmin/restaurant/profile?phone=${encodeURIComponent(phone)}&_t=${Date.now()}`),
        getData('subscriptions')
      ]);

      console.log('MOU Data:', resMou);
      console.log('Restaurant Data:', resRest);
      console.log('Subscriptions Data:', resSubs);

      // Set MOU data
      if (resMou.success) {
        setMouData(resMou.data);
      }

      // Set subscriptions
      if (resSubs.success && resSubs.data?.length) {
        setSubscriptions(resSubs.data);
      }

      // Set restaurant data
      if (resRest.success) {
        const data = resRest.data;
        setRestaurant(data);
        setIsScheduleApproved(data.is_schedule_approved === 1 || data.is_schedule_approved === true);
        setIsMouAccepted(Number(data.is_mou) === 1 || data.is_mou === true);
        setScheduleMode(data.is_auto_schedule ? 'auto' : 'manual');
        setShiftStart(data.shift_start || '10:00');
        setShiftEnd(data.shift_end || '22:00');

        // Set subscription plan
        const planId = data.subscription_id || 3;
        if (resSubs.success && resSubs.data?.length) {
          const plan = resSubs.data.find(p => p.id == planId);
          setSelectedPlan(plan || resSubs.data[0]);
        } else {
          setSelectedPlan({ id: 3, name: 'Monthly', price: '299' });
        }
      } else {
        // Fallback if restaurant fetch fails
        if (resSubs.success && resSubs.data?.length) {
          const defaultPlan = resSubs.data.find(p => p.id == 3) || resSubs.data[0];
          setSelectedPlan(defaultPlan);
        } else {
          setSelectedPlan({ id: 3, name: 'Monthly', price: '299' });
        }
        setIsMouAccepted(false);
      }
    } catch (err) {
      console.error('Load data error:', err);
      setError(err.message || 'Failed to load data');
      // Set default plan on error
      setSelectedPlan({ id: 3, name: 'Monthly', price: '299' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Submit schedule
  const handleSubmitSchedule = async () => {
    if (!restaurant) {
      alert('Please complete registration first.');
      return;
    }

    setSubmittingSchedule(true);
    try {
      const userData = getUserData();
      const phone = userData?.phone || userData?.contact || '';
      
      const payload = {
        phone,
        is_auto_schedule: scheduleMode === 'auto' ? 1 : 0,
        shift_start: shiftStart,
        shift_end: shiftEnd
      };

      const response = await putData('vandersuperadmin/restaurant/update-schedule', payload);
      
      if (response.success) {
        alert('Schedule submitted for approval.');
        setIsScheduleApproved(false);
        await loadData();
      } else {
        alert(response.message || 'Failed to submit schedule');
      }
    } catch (error) {
      console.error('Submit schedule error:', error);
      alert('Something went wrong.');
    } finally {
      setSubmittingSchedule(false);
    }
  };

  // Accept MOU
  const handleAcceptMou = async () => {
    if (isMouAccepted) return;

    setSubmitting(true);
    try {
      const userData = getUserData();
      const phone = userData?.phone || userData?.contact || '';

      const response = await putData('vandersuperadmin/restaurant/accept-mou', { phone });
      
      if (response.success) {
        alert('MOU accepted successfully.');
        setIsMouAccepted(true);
        setRestaurant(prev => ({ ...prev, is_mou: 1 }));
        await loadData();
      } else {
        alert(response.message || 'Failed to accept MOU');
      }
    } catch (error) {
      console.error('Accept MOU error:', error);
      alert('Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  // Strip HTML
  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&');
  };

  // Get status display
  const getStatusDisplay = () => {
    if (isMouAccepted) {
      return { label: 'Accepted', color: theme.success, icon: <FaCheckCircle /> };
    }
    if (isScheduleApproved) {
      return { label: 'Ready to Accept', color: theme.warning, icon: <FaClock /> };
    }
    return { label: 'Pending', color: theme.textSecondary, icon: <FaClock /> };
  };

  // Styles
  const styles = {
    pageWrapper: {
      minHeight: '100vh',
      backgroundColor: theme.bg,
      padding: '2rem',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      transition: 'background-color 0.3s ease, color 0.3s ease',
    },
    container: {
      maxWidth: '1100px',
      margin: '0 auto',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    backBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      background: theme.cardBg,
      border: `1px solid ${theme.border}`,
      padding: '0.5rem 1.2rem',
      borderRadius: '40px',
      fontSize: '0.85rem',
      fontWeight: '500',
      color: theme.text,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    headerTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: theme.text,
    },
    headerSubtitle: {
      fontSize: '0.8rem',
      color: theme.textSecondary,
      marginTop: '0.1rem',
    },
    headerRight: {
      display: 'flex',
      gap: '0.75rem',
    },
    actionBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.4rem',
      background: theme.cardBg,
      border: `1px solid ${theme.border}`,
      padding: '0.5rem 1.2rem',
      borderRadius: '40px',
      fontSize: '0.8rem',
      fontWeight: '500',
      color: theme.text,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh',
      gap: '1rem',
    },
    loadingSpinner: {
      animation: 'spin 1s linear infinite',
      fontSize: '2.5rem',
      color: theme.primary,
    },
    errorContainer: {
      textAlign: 'center',
      padding: '3rem 2rem',
      background: theme.cardBg,
      borderRadius: '16px',
      border: `1px solid ${isDark ? 'rgba(220, 38, 38, 0.3)' : '#fecaca'}`,
    },
    errorIcon: {
      fontSize: '3rem',
      color: '#dc2626',
      marginBottom: '1rem',
    },
    mainGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 340px',
      gap: '1.5rem',
    },
    leftColumn: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem',
    },
    rightColumn: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem',
    },
    card: {
      backgroundColor: theme.cardBg,
      borderRadius: '20px',
      padding: '1.75rem',
      boxShadow: theme.shadow,
      border: `1px solid ${theme.border}`,
      transition: 'all 0.3s ease',
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.25rem',
      paddingBottom: '0.75rem',
      borderBottom: `2px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f0f4f8'}`,
    },
    cardTitle: {
      fontSize: '0.9rem',
      fontWeight: '700',
      color: theme.text,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    cardBadge: (status) => ({
      fontSize: '0.6rem',
      fontWeight: '600',
      padding: '0.2rem 0.8rem',
      borderRadius: '40px',
      backgroundColor: status === 'Accepted' ? (isDark ? 'rgba(22, 163, 74, 0.15)' : '#f0fdf4') : (isDark ? 'rgba(245, 158, 11, 0.15)' : '#fff7ed'),
      color: status === 'Accepted' ? theme.success : theme.warning,
      display: 'flex',
      alignItems: 'center',
      gap: '0.3rem',
    }),
    mouHeader: {
      textAlign: 'center',
      marginBottom: '2rem',
      paddingBottom: '1.5rem',
      borderBottom: `2px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f0f4f8'}`,
    },
    mouIcon: {
      width: '64px',
      height: '64px',
      borderRadius: '50%',
      background: theme.primaryGradient,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '2rem',
      margin: '0 auto 1rem',
      boxShadow: '0 8px 16px rgba(249, 115, 22, 0.3)',
    },
    mouTitle: {
      fontSize: '1.6rem',
      fontWeight: '700',
      color: theme.text,
      marginBottom: '0.25rem',
    },
    mouSubtitle: {
      fontSize: '0.9rem',
      color: theme.textSecondary,
    },
    mouPartner: {
      display: 'inline-flex',
      alignItems: 'center',
      backgroundColor: theme.accentBg,
      padding: '0.3rem 1.2rem',
      borderRadius: '40px',
      fontSize: '0.85rem',
      fontWeight: '600',
      color: theme.primary,
      marginTop: '0.5rem',
      border: `1px solid ${theme.border}`,
    },
    section: {
      marginBottom: '1.5rem',
    },
    sectionTitle: {
      fontSize: '0.85rem',
      fontWeight: '600',
      color: theme.text,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginBottom: '0.5rem',
    },
    sectionIcon: {
      color: theme.primary,
      fontSize: '1rem',
    },
    sectionContent: {
      fontSize: '0.85rem',
      color: theme.textSecondary,
      lineHeight: '1.7',
      paddingLeft: '1.8rem',
    },
    planCard: {
      background: theme.primaryGradient,
      borderRadius: '16px',
      padding: '1.25rem 1.5rem',
      color: 'white',
      marginBottom: '1.5rem',
      boxShadow: '0 8px 16px rgba(249, 115, 22, 0.25)',
    },
    planLabel: {
      fontSize: '0.7rem',
      fontWeight: '500',
      opacity: 0.9,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    planName: {
      fontSize: '1.4rem',
      fontWeight: '700',
      marginTop: '0.2rem',
    },
    planPrice: {
      fontSize: '2rem',
      fontWeight: '700',
      marginTop: '0.3rem',
    },
    planPriceSub: {
      fontSize: '0.8rem',
      opacity: 0.9,
    },
    planStatus: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.3rem',
      backgroundColor: 'rgba(255,255,255,0.2)',
      padding: '0.2rem 0.8rem',
      borderRadius: '40px',
      fontSize: '0.65rem',
      fontWeight: '600',
      marginTop: '0.5rem',
    },
    tabs: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '1.25rem',
      borderBottom: `2px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f0f4f8'}`,
      paddingBottom: '0.5rem',
    },
    tab: {
      padding: '0.4rem 1.2rem',
      borderRadius: '40px',
      fontSize: '0.8rem',
      fontWeight: '500',
      color: theme.textSecondary,
      cursor: 'pointer',
      transition: 'all 0.2s',
      background: 'transparent',
      border: 'none',
    },
    tabActive: {
      backgroundColor: theme.accentBg,
      color: theme.primary,
      fontWeight: '600',
    },
    chargesGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '0.75rem',
      marginTop: '0.5rem',
    },
    chargeItem: {
      backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc',
      borderRadius: '12px',
      padding: '0.75rem 1rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'transparent'}`,
    },
    chargeLabel: {
      fontSize: '0.75rem',
      color: theme.textSecondary,
    },
    chargeValue: {
      fontSize: '0.85rem',
      fontWeight: '600',
      color: theme.text,
    },
    scheduleSection: {
      backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc',
      borderRadius: '16px',
      padding: '1.25rem',
      marginTop: '1rem',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'transparent'}`,
    },
    scheduleToggle: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '1rem',
    },
    toggleBtn: (isActive) => ({
      flex: 1,
      padding: '0.5rem',
      border: `2px solid ${isActive ? theme.primary : theme.border}`,
      borderRadius: '10px',
      background: isActive ? theme.accentBg : theme.cardBg,
      fontSize: '0.8rem',
      fontWeight: '600',
      color: isActive ? theme.primary : theme.textSecondary,
      cursor: 'pointer',
      transition: 'all 0.2s',
      textAlign: 'center',
    }),
    scheduleRow: {
      display: 'flex',
      gap: '1rem',
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    scheduleInput: {
      flex: 1,
      padding: '0.5rem 0.75rem',
      border: `1px solid ${theme.border}`,
      borderRadius: '10px',
      fontSize: '0.85rem',
      outline: 'none',
      minWidth: '100px',
      backgroundColor: theme.cardBg,
      color: theme.text,
    },
    scheduleLabel: {
      fontSize: '0.75rem',
      color: theme.textSecondary,
      fontWeight: '500',
      marginBottom: '0.25rem',
      display: 'block',
    },
    submitBtn: {
      width: '100%',
      padding: '0.75rem',
      backgroundColor: theme.primary,
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: '0.85rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      marginTop: '1rem',
      boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
    },
    approvedBadge: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.5rem',
      backgroundColor: isDark ? 'rgba(22, 163, 74, 0.15)' : '#f0fdf4',
      color: theme.success,
      borderRadius: '12px',
      fontSize: '0.8rem',
      fontWeight: '600',
      marginTop: '0.75rem',
      border: `1px solid ${isDark ? 'rgba(22, 163, 74, 0.3)' : '#bbf7d0'}`,
    },
    summaryItem: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '0.6rem 0',
      borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f0f4f8'}`,
    },
    summaryLabel: {
      fontSize: '0.8rem',
      color: theme.textSecondary,
    },
    summaryValue: {
      fontSize: '0.85rem',
      fontWeight: '600',
      color: theme.text,
    },
    acceptSection: {
      marginTop: '1.5rem',
      paddingTop: '1.5rem',
      borderTop: `2px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f0f4f8'}`,
    },
    acceptBtn: (isAccepted) => ({
      width: '100%',
      padding: '1rem',
      backgroundColor: isAccepted ? theme.success : theme.primary,
      color: 'white',
      border: 'none',
      borderRadius: '14px',
      fontSize: '1rem',
      fontWeight: '700',
      cursor: isAccepted ? 'default' : 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.75rem',
      opacity: isAccepted ? 0.9 : 1,
      boxShadow: isAccepted ? 'none' : '0 4px 12px rgba(249, 115, 22, 0.3)',
    }),
    acceptInfo: {
      fontSize: '0.75rem',
      color: theme.textSecondary,
      textAlign: 'center',
      marginTop: '0.5rem',
    },
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.pageWrapper}>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        <div style={styles.container}>
          <div style={styles.loadingContainer}>
            <FaSpinner style={styles.loadingSpinner} />
            <div style={{ color: theme.textSecondary, fontSize: '1rem' }}>Loading agreement details...</div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={styles.pageWrapper}>
        <div style={styles.container}>
          <div style={styles.errorContainer}>
            <FaExclamationTriangle style={styles.errorIcon} />
            <h3 style={{ color: theme.text, marginBottom: '0.5rem' }}>Failed to load agreement</h3>
            <p style={{ color: theme.textSecondary }}>{error}</p>
            <button
              onClick={loadData}
              style={{
                marginTop: '1rem',
                padding: '0.5rem 2rem',
                background: theme.primaryGradient,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const status = getStatusDisplay();
  const restaurantName = restaurant?.organisation_name || restaurant?.name || 'Your Restaurant';
  const planName = selectedPlan?.name || 'Monthly';
  const planPrice = selectedPlan?.price || '299';

  // Charges data
  const charges = restaurant || {
    comission: 20,
    special_comission: 10,
    min_order: 200,
    platform_charge: 9,
    handling_charge: 2,
    gst_percentage: 5,
    order_receiving_charges: 1,
  };

  return (
    <div style={styles.pageWrapper}>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .back-btn:hover {
          background: ${isDark ? 'rgba(249, 115, 22, 0.15)' : '#ffedd5'} !important;
          transform: translateX(-2px);
        }
        .action-btn:hover {
          background: ${isDark ? 'rgba(249, 115, 22, 0.15)' : '#ffedd5'} !important;
        }
        .tab:hover {
          background: ${isDark ? 'rgba(249, 115, 22, 0.1)' : '#fff7ed'} !important;
        }
        .submit-btn:hover:not(:disabled) {
          background: #ea580c !important;
          transform: translateY(-1px);
        }
        .accept-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(249, 115, 22, 0.4) !important;
        }
      `}</style>

      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <button className="back-btn" style={styles.backBtn} onClick={() => navigate(-1)}>
              <FaArrowLeft /> Back to Dashboard
            </button>
          </div>
        </div>

        {/* Main Grid */}
        <div style={styles.mainGrid}>
          {/* Left Column */}
          <div style={styles.leftColumn}>
            {/* Main Agreement Card */}
            <div style={styles.card}>
              {/* Tabs */}
              <div style={styles.tabs}>
                <button 
                  className="tab"
                  style={{ ...styles.tab, ...(activeTab === 'overview' ? styles.tabActive : {}) }}
                  onClick={() => setActiveTab('overview')}
                >
                  Overview
                </button>
                <button 
                  className="tab"
                  style={{ ...styles.tab, ...(activeTab === 'terms' ? styles.tabActive : {}) }}
                  onClick={() => setActiveTab('terms')}
                >
                  Terms
                </button>
              </div>

              {/* MOU Header */}
              <div style={styles.mouHeader}>
                <div style={styles.mouIcon}>
                  <FaHandshake />
                </div>
                <div style={styles.mouTitle}>Memorandum of Understanding</div>
                <div style={styles.mouSubtitle}>Zaptodoor Partner Agreement</div>
                <div style={styles.mouPartner}>
                  <FaStore style={{ marginRight: '0.3rem' }} />
                  {restaurantName}
                </div>
              </div>

              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <>
                  {/* Greeting */}
                  <div style={styles.section}>
                    <div style={styles.sectionTitle}>
                      <FaInfoCircle style={styles.sectionIcon} />
                      Hello Partner,
                    </div>
                    <div style={styles.sectionContent}>
                      {mouData?.greeting || 'Greetings from Zaptodoor! We invite your restaurant to join our food delivery platform and reach more customers in your area.'}
                    </div>
                  </div>

                  {/* Subscription Plan */}
                  <div style={styles.planCard}>
                    <div style={styles.planLabel}>Your Subscription Plan</div>
                    <div style={styles.planName}>{planName} Plan</div>
                    <div style={styles.planPrice}>
                      ₹ {planPrice} <span style={styles.planPriceSub}>/ month</span>
                    </div>
                    <div style={styles.planStatus}>
                      <FaCheckCircle style={{ fontSize: '0.7rem' }} />
                      {isMouAccepted ? 'Active — Will be renewed periodically' : 'Pending Acceptance'}
                    </div>
                  </div>

                  {/* Partnership Terms */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div style={styles.section}>
                      <div style={styles.sectionTitle}>
                        <FaBuilding style={styles.sectionIcon} />
                        Restaurant Obligations
                      </div>
                      <div style={styles.sectionContent}>
                        {mouData?.restaurant_obligations || 'Restaurants must ensure quality food, proper packaging, and timely order preparation.'}
                      </div>
                    </div>
                    <div style={styles.section}>
                      <div style={styles.sectionTitle}>
                        <FaTruck style={styles.sectionIcon} />
                        Zaptodoor Obligations
                      </div>
                      <div style={styles.sectionContent}>
                        {mouData?.platform_obligations || 'We provide order support, platform visibility, and promotional opportunities to grow your business.'}
                      </div>
                    </div>
                  </div>

                  {/* Additional Terms */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                    <div style={styles.section}>
                      <div style={styles.sectionTitle}>
                        <FaCreditCard style={styles.sectionIcon} />
                        Commission & Payment
                      </div>
                      <div style={styles.sectionContent}>
                        Charges apply per order as per platform policy and payments are settled as per the billing cycle.
                      </div>
                    </div>
                    <div style={styles.section}>
                      <div style={styles.sectionTitle}>
                        <FaGavel style={styles.sectionIcon} />
                        Dispute Resolution
                      </div>
                      <div style={styles.sectionContent}>
                        Any dispute will be resolved through mutual discussion and applicable legal process in Gwalior Only.
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Terms Tab */}
              {activeTab === 'terms' && (
                <>
                  <div style={styles.section}>
                    <div style={styles.sectionTitle}>
                      <FaHandshake style={styles.sectionIcon} />
                      Partnership Terms
                    </div>
                    <div style={styles.sectionContent}>
                      {mouData && mouData.sections ? (
                        mouData.sections.map((sec, idx) => {
                          const titleLower = sec.title?.toLowerCase() || '';
                          if (titleLower.includes('charges') || titleLower.includes('platform')) return null;
                          return (
                            <div key={idx} style={{ marginBottom: '1rem' }}>
                              <strong>{sec.title}</strong>
                              <p>{stripHtml(sec.content)}</p>
                            </div>
                          );
                        })
                      ) : (
                        <>
                          <p><strong>Obligations of the Restaurant Partner:</strong><br />
                          Restaurants must ensure quality food, proper packaging, and timely order preparation.</p>
                          <br />
                          <p><strong>Obligations of Zaptodoor:</strong><br />
                          We provide order support, platform visibility, and promotional opportunities to grow your business.</p>
                          <br />
                          <p><strong>Commission & Payment Terms:</strong><br />
                          Charges apply per order as per platform policy and payments are settled as per the billing cycle.</p>
                          <br />
                          <p><strong>Termination Policy:</strong><br />
                          Either party may end the partnership with prior notice if terms are not followed.</p>
                          <br />
                          <p><strong>Confidentiality:</strong><br />
                          Both parties will keep business and operational information confidential.</p>
                          <br />
                          <p><strong>Dispute Resolution:</strong><br />
                          Any dispute will be resolved through mutual discussion and applicable legal process in Gwalior Only.</p>
                          <br />
                          <p><em>We look forward to partnering with you and growing your restaurant business together.</em></p>
                          <p style={{ fontWeight: '600', color: theme.text, marginTop: '0.5rem' }}>Team Zaptodoor</p>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Charges */}
                  <div style={styles.section}>
                    <div style={styles.sectionTitle}>
                      <FaExclamationTriangle style={{ ...styles.sectionIcon, color: theme.warning }} />
                      Charges & Commission Update
                    </div>
                    <div style={styles.sectionContent}>
                      Zaptodoor may revise commission and platform charges anytime according to company policy.
                    </div>
                    <div style={styles.chargesGrid}>
                      <div style={styles.chargeItem}>
                        <span style={styles.chargeLabel}>Commission</span>
                        <span style={styles.chargeValue}>{charges.comission}%</span>
                      </div>
                      <div style={styles.chargeItem}>
                        <span style={styles.chargeLabel}>Platform Charge</span>
                        <span style={styles.chargeValue}>₹{charges.platform_charge}</span>
                      </div>
                      <div style={styles.chargeItem}>
                        <span style={styles.chargeLabel}>Handling Charge</span>
                        <span style={styles.chargeValue}>{charges.handling_charge}%</span>
                      </div>
                      <div style={styles.chargeItem}>
                        <span style={styles.chargeLabel}>GST</span>
                        <span style={styles.chargeValue}>{charges.gst_percentage}%</span>
                      </div>
                      <div style={styles.chargeItem}>
                        <span style={styles.chargeLabel}>Order Receiving</span>
                        <span style={styles.chargeValue}>₹{charges.order_receiving_charges}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Schedule Section */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.cardTitle}>
                  <FaClock /> Operating Schedule
                </div>
              </div>
              <div style={styles.scheduleSection}>
                <div style={styles.scheduleToggle}>
                  <button
                    style={styles.toggleBtn(scheduleMode === 'manual')}
                    onClick={() => setScheduleMode('manual')}
                  >
                    Manual
                  </button>
                  <button
                    style={styles.toggleBtn(scheduleMode === 'auto')}
                    onClick={() => setScheduleMode('auto')}
                  >
                    Auto
                  </button>
                </div>

                <div style={styles.scheduleRow}>
                  <div style={{ flex: 1 }}>
                    <div style={styles.scheduleLabel}>Shift Start</div>
                    <input
                      type="time"
                      style={styles.scheduleInput}
                      value={shiftStart}
                      onChange={(e) => setShiftStart(e.target.value)}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={styles.scheduleLabel}>Shift End</div>
                    <input
                      type="time"
                      style={styles.scheduleInput}
                      value={shiftEnd}
                      onChange={(e) => setShiftEnd(e.target.value)}
                    />
                  </div>
                </div>

                <button
                  className="submit-btn"
                  style={styles.submitBtn}
                  onClick={handleSubmitSchedule}
                  disabled={submittingSchedule}
                >
                  {submittingSchedule ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : 'Submit Schedule for Approval'}
                </button>

                <div style={styles.approvedBadge}>
                  {isScheduleApproved ? (
                    <>
                      <FaCheckCircle /> Schedule Approved
                    </>
                  ) : (
                    <>
                      <FaClock /> Waiting for Admin Approval
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div style={styles.rightColumn}>
            {/* Summary Card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.cardTitle}>
                  <FaFileSignature /> Agreement Summary
                </div>
                <span style={styles.cardBadge(isMouAccepted ? 'Accepted' : 'Pending')}>
                  {isMouAccepted ? <FaCheckCircle size={10} /> : <FaClock size={10} />} {isMouAccepted ? 'Accepted' : 'Pending'}
                </span>
              </div>

              <div>
                <div style={styles.summaryItem}>
                  <span style={styles.summaryLabel}>Partner</span>
                  <span style={styles.summaryValue}>{restaurantName}</span>
                </div>
                <div style={styles.summaryItem}>
                  <span style={styles.summaryLabel}>Plan</span>
                  <span style={styles.summaryValue}>{planName}</span>
                </div>
                <div style={styles.summaryItem}>
                  <span style={styles.summaryLabel}>Status</span>
                  <span style={{ ...styles.summaryValue, color: isMouAccepted ? theme.success : theme.warning }}>
                    {isMouAccepted ? (
                      <><FaCheckCircle style={{ fontSize: '0.6rem', marginRight: '0.3rem' }} /> Accepted</>
                    ) : (
                      <><FaClock style={{ fontSize: '0.6rem', marginRight: '0.3rem' }} /> Pending</>
                    )}
                  </span>
                </div>
              </div>

              <div style={{ margin: '1rem 0', borderTop: `1px solid ${theme.border}` }} />

              <div style={{ marginTop: '1rem' }}>
                <div style={{ ...styles.sectionTitle, marginBottom: '0.75rem' }}>
                  <FaCreditCard style={styles.sectionIcon} /> Charges & Commission
                </div>
                <div style={styles.chargesGrid}>
                  <div style={styles.chargeItem}>
                    <span style={styles.chargeLabel}>Commission</span>
                    <span style={{ ...styles.chargeValue, color: theme.primary }}>
                      {charges.comission}%
                    </span>
                  </div>
                  <div style={styles.chargeItem}>
                    <span style={styles.chargeLabel}>Platform Charge</span>
                    <span style={{ ...styles.chargeValue, color: theme.primaryLight }}>
                      ₹{charges.platform_charge}
                    </span>
                  </div>
                  <div style={styles.chargeItem}>
                    <span style={styles.chargeLabel}>Handling Charge</span>
                    <span style={{ ...styles.chargeValue, color: theme.warning }}>
                      {charges.handling_charge}%
                    </span>
                  </div>
                  <div style={styles.chargeItem}>
                    <span style={styles.chargeLabel}>GST</span>
                    <span style={{ ...styles.chargeValue, color: theme.success }}>
                      {charges.gst_percentage}%
                    </span>
                  </div>
                  <div style={styles.chargeItem}>
                    <span style={styles.chargeLabel}>Order Receiving</span>
                    <span style={{ ...styles.chargeValue, color: '#3b82f6' }}>
                      ₹{charges.order_receiving_charges}
                    </span>
                  </div>
                </div>
              </div>

              {isMouAccepted && (
                <div style={{ 
                  marginTop: '1rem', 
                  fontSize: '0.75rem', 
                  color: theme.success,
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}>
                  <FaCheckCircle size={12} />
                  <span>Agreement accepted on {new Date().toLocaleDateString()}</span>
                </div>
              )}
            </div>

            {/* Accept Agreement */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.cardTitle}>
                  <FaCheckCircle /> Acceptance
                </div>
              </div>

              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  fontSize: '0.85rem', 
                  color: theme.textSecondary, 
                  marginBottom: '1rem',
                  lineHeight: '1.6'
                }}>
                  By accepting this agreement, you agree to all the terms and conditions 
                  mentioned in the Memorandum of Understanding.
                </div>

                <button 
                  className="accept-btn"
                  style={styles.acceptBtn(isMouAccepted)}
                  onClick={handleAcceptMou}
                  disabled={isMouAccepted || submitting}
                >
                  {submitting ? (
                    <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                  ) : isMouAccepted ? (
                    <>
                      <FaCheckCircle /> MOU AGREEMENT ACCEPTED
                    </>
                  ) : (
                    <>
                      <FaFileSignature /> Accept MOU Agreement
                    </>
                  )}
                </button>

                {isMouAccepted ? (
                  <div style={{ 
                    marginTop: '0.75rem', 
                    fontSize: '0.75rem', 
                    color: theme.success,
                    fontWeight: '500'
                  }}>
                    <FaCheckCircle style={{ marginRight: '0.3rem' }} />
                    Agreement accepted on {new Date().toLocaleDateString()}
                  </div>
                ) : (
                  <div style={styles.acceptInfo}>
                    {!isScheduleApproved && 'Please wait for schedule approval to accept MOU'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MOUAgreement;